/**
 * HTTP Retry Strategy
 * Handles retry logic for specific HTTP status codes and network errors
 */

import env from '@/config/env';

const RETRYABLE_STATUS_CODES = [408, 429, 502, 503, 504];
const NON_RETRYABLE_STATUS_CODES = [400, 401, 403, 404, 418, 422, 501, 505];
const RETRYABLE_NETWORK_ERROR_CODES = [
  'ENOTFOUND',
  'EAI_AGAIN',
  'ECONNRESET',
  'EPIPE',
  'ECONNABORTED',
  'ETIMEDOUT',
];

export const isRetryableStatusCode = (status) => RETRYABLE_STATUS_CODES.includes(status);
export const isNonRetryableStatusCode = (status) => NON_RETRYABLE_STATUS_CODES.includes(status);

/**
 * Check if error is a timeout
 * Detects actual timeout scenarios (not manual aborts):
 * - ECONNABORTED: Axios/Node.js timeout indicator
 * - ETIMEDOUT: Node.js network timeout
 * - "timeout" in message: ONLY for non-AbortError (manual aborts excluded)
 *
 * @param {object} error - Error object
 * @returns {boolean} - True if error is timeout-related
 */
export const isTimeoutError = (error) => {
  if (!error) return false;
  if (error.code === 'ECONNABORTED') return true;
  if (error.code === 'ETIMEDOUT') return true;
  if (error.message?.toLowerCase().includes('timeout')) return true;
  return false;
};

/**
 * Check if error is a CORS error
 * Detects cross-origin resource sharing errors
 *
 * @param {object} error - Error object
 * @returns {boolean} - True if error is CORS-related
 */
export const isCorsError = (error) => {
  if (!error) return false;

  const msg = typeof error === 'string' ? error.toLowerCase() : error.message?.toLowerCase();

  if (!msg) return false;
  return (
    msg.includes('cors') ||
    msg.includes('no allow credentials') ||
    msg.includes('failed to fetch') ||
    msg === 'failed to fetch'
  );
};

/**
 * Extract retry flags from response body
 * Server can override retry logic with retryable and retryAfterSeconds
 *
 * @param {Response|object} response - Response object or error.response
 * @returns {Promise<{retryable: boolean|null, retryAfterSeconds: number|null}>}
 */
export const extractRetryFlags = async (response) => {
  const flags = {
    retryable: null,
    retryAfterSeconds: null,
  };

  if (!response) return flags;

  try {
    // Handle both Response objects and already-parsed data
    const data =
      response.data || (typeof response.json === 'function' ? await response.clone().json() : null);

    if (data && typeof data === 'object') {
      if (typeof data.retryable === 'boolean') flags.retryable = data.retryable;
      if (typeof data.retryAfterSeconds === 'number' && data.retryAfterSeconds > 0) {
        flags.retryAfterSeconds = data.retryAfterSeconds;
      }
    }
  } catch (e) {}

  return flags;
};

/**
 * Check if error is a retryable network error
 * Detects connection failures, DNS errors
 * NOTE: CORS is NOT retryable - it's a configuration error
 * @param {Error} error - Error object
 * @returns {boolean} - True if network error should be retried
 */
export const isRetryableNetworkError = async (error) => {
  if (!error) return false;

  let isRetryable = false;

  if (error.code && RETRYABLE_NETWORK_ERROR_CODES.includes(error.code)) isRetryable = true;
  else if (error instanceof TypeError && error.message) {
    const msg = error.message?.toLowerCase();
    if (
      msg.includes('failed to fetch') ||
      msg.includes('network request failed') ||
      msg.includes('networkerror') ||
      msg.includes('load failed')
    ) {
      return true;
    }
  }

  return isRetryable;
};

/**
 * Extract URL from error or request object
 * @param {object|Error} error - Error object with request property
 * @returns {string|null} - URL string or null
 */
const extractUrl = (error) => {
  if (!error) return null;
  if (error.url) return error.url;
  if (error.request?.url) return error.request.url;
  if (error.config?.url) return error.config.url;
  return null;
};

/**
 * Check if request should skip retry based on URL and error conditions
 * @param {string} url - Request URL
 * @param {object|Error} error - Error object
 * @returns {boolean} - True if retry should be skipped
 */
const shouldSkipRetry = (url, error) => {
  if (!url) return false;

  const isImportTableDataRequest = url.includes('/processInstance/import/form/tableData');
  if (!isImportTableDataRequest) return false;

  const response = error?.response || (error?.status ? error : null);
  const status = response?.status;
  const isTimeoutErr = isTimeoutError(error);

  // 256 - PR Testing
  const shouldSkip = status === 504 || status === 499 || isTimeoutErr;
  return shouldSkip;
};

/**
 * Determines if an HTTP error should be retried
 * Checks response body for server-provided retryable flag first
 * FALLBACK: Uses hardcoded status codes if flag not present
 *
 * @param {object|Error} error - Error object (may have response property) or raw Response
 * @param {number} retryCount - Current retry attempt count
 * @param {object} serverFlags - Optional pre-extracted server flags {retryable, retryAfterSeconds}
 * @param {object} options - Optional request options that may contain retryCount
 * @returns {boolean} - True if request should be retried
 */
export const shouldRetryError = (error, retryCount = 0, serverFlags = null, options = null) => {
  // Use options.retryCount as fallback if main retryCount is 0
  const hasRetryCount = typeof retryCount === 'number' && !Number.isNaN(retryCount);
  const effectiveRetryCount = hasRetryCount
    ? retryCount
    : typeof options?.retryCount === 'number'
    ? options.retryCount
    : 0;

  if (effectiveRetryCount >= env.MAX_RETRY_ATTEMPTS) return false;

  // Check if retry should be skipped for specific URL patterns and error conditions
  const url = extractUrl(error);
  if (shouldSkipRetry(url, error)) return false;

  if (serverFlags && typeof serverFlags.retryable === 'boolean') return serverFlags.retryable;
  if (isTimeoutError(error)) return true;

  // Handle case where error is actually a Response object (backward compatibility)
  const response = error?.response || (error?.status ? error : null);

  if (response?.status) {
    if (isNonRetryableStatusCode(response.status)) return false;
    if (isRetryableStatusCode(response.status)) return true;
    return false;
  }

  return isRetryableNetworkError(error);
};

/**
 * Calculate exponential backoff with jitter for retry attempts
 * Accepts server-provided retryAfterSeconds to override default backoff
 *
 * Formula: min(maxDelay, baseDelay * 2^attempt) + random(0, jitter)
 *
 * @param {number} retryCount - Current retry attempt (0-based)
 * @param {number|null} serverRetryAfterSeconds - Optional server-provided delay in seconds
 * @returns {number} - Delay in milliseconds with jitter applied
 */
export const calculateRetryDelay = (retryCount = 0, serverRetryAfterSeconds = null) => {
  if (typeof serverRetryAfterSeconds === 'number' && serverRetryAfterSeconds > 0) {
    return serverRetryAfterSeconds * 1000;
  }

  // FALLBACK: Exponential backoff with jitter
  const BASE_DELAY = 500;
  const MAX_DELAY = 5000;
  const JITTER_RANGE = 400;

  if (retryCount === 0) return 0;

  const exponentialDelay = BASE_DELAY * Math.pow(2, retryCount);
  const cappedDelay = Math.min(exponentialDelay, MAX_DELAY);

  // Add full jitter: random value between 0 and JITTER_RANGE
  const jitter = Math.random() * JITTER_RANGE;
  const finalDelay = Math.round(cappedDelay + jitter);

  return finalDelay;
};

/**
 * Extract retry metadata from error/response for telemetry
 * Checks for Retry-After header and other retry-related info
 *
 * @param {object} error - Error object with response
 * @returns {object} - Retry metadata (retryAfter, retryable, etc.)
 */
export const extractRetryMetadata = (error) => {
  const metadata = {
    retryAfter: null,
    retryable: false,
    isNetworkError: false,
  };

  if (!error) return metadata;

  metadata.isNetworkError = isRetryableNetworkError(error);

  // Extract response metadata if available
  const response = error?.response;
  if (!response) return metadata;

  // Check Retry-After header
  const retryAfter = response.headers?.get?.('Retry-After');

  if (retryAfter) metadata.retryAfter = parseInt(retryAfter, 10) || retryAfter;
  if (response.status && isRetryableStatusCode(response.status)) {
    metadata.retryable = true;
  }

  return metadata;
};

/**
 * Attach retry metadata to error object for downstream handling
 * Used to communicate retry state to error handler and telemetry
 *
 * @param {object} error - Original error object
 * @param {number} retryCount - Number of retry attempts made
 * @param {boolean} maxRetriesReached - Whether max retries were exhausted
 * @returns {object} - Error with retry metadata attached
 */
export const attachRetryMetadata = (error, retryCount, maxRetriesReached = false) => {
  error.retryCount = retryCount;
  error.maxRetriesReached = maxRetriesReached;
  error.retryMetadata = extractRetryMetadata(error);
  return error;
};

/**
 * Wait for specified delay (async helper)
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
export const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
